// frontend/src/services/apiClient.js
import axios from 'axios';

const isDevelopment = process.env.NODE_ENV === 'development';
const useMockAuth = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';

// In development, always use the CRA proxy so the browser stays same-origin.
// This avoids CORS and "localhost" mismatches when the frontend is opened via LAN IP.
const API_BASE = isDevelopment
  ? '/api/v1'
  : process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/v1`
    : '/api/v1';

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000, // 30-second timeout
  withCredentials: true, // SECURITY: Enable cookies for httpOnly authentication
});

// Helper to read CSRF token from cookie
const getCsrfToken = () => {
  const match = document.cookie.match(/(^|; )csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
};

// SECURITY: Automatically include CSRF token on state-changing requests
apiClient.interceptors.request.use((config) => {
  // Let the browser set multipart boundary for FormData uploads.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }

  // Force HTTPS in production for absolute URLs
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    // If the URL is absolute and starts with http://, replace with https://
    if (config.url && config.url.startsWith('http://')) {
      config.url = config.url.replace('http://', 'https://');
    }
    // If baseURL is set and starts with http://, replace with https://
    if (config.baseURL && config.baseURL.startsWith('http://')) {
      config.baseURL = config.baseURL.replace('http://', 'https://');
    }
  }
  
  // Check if this is a login request (CSRF exempted endpoints)
  const isLoginRequest = config.url && (
    config.url.includes('/auth/login') || 
    config.url.includes('/auth/refresh') ||
    config.url.includes('/waiting-list/join')
  );
  
  const csrf = getCsrfToken();
  if (csrf && config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase()) && !isLoginRequest) {
    config.headers['X-CSRF-Token'] = csrf;
  }

  // Dev: avoid stale API responses after backend restarts (browser/proxy heuristics).
  if (isDevelopment) {
    const m = (config.method || 'get').toLowerCase();
    if (m === 'get' || m === 'head') {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }
  }
  
  // SECURITY: Prevent sensitive data from being logged in browser dev tools
  if (config.url && config.url.includes('/auth/login')) {
    // Create a copy of config without sensitive data for logging
    const safeConfig = { ...config };
    if (safeConfig.data) {
      safeConfig.data = '[REDACTED - Login credentials]';
    }
    // Don't log the actual config for login requests
  }
  
  return config;
});

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (useMockAuth) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Make refresh request using appropriate URL
        const refreshURL = isDevelopment
          ? '/api/v1/auth/refresh'
          : process.env.REACT_APP_API_URL
            ? `${process.env.REACT_APP_API_URL}/v1/auth/refresh`
            : '/api/v1/auth/refresh';

        await axios.post(refreshURL, null, {
          withCredentials: true
          // Note: refresh is CSRF-exempted, no token needed
        });

        // Retry original request with cookies
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Handle refresh token error
        console.error('Token refresh failed:', refreshError);

        // Clear identifier and redirect to login page (avoid infinite loops)
        localStorage.removeItem('user_id');
        
        // Only redirect to login if we're not on a public page and the request wasn't /auth/me
        const publicRoutes = ['/', '/login', '/tutorial', '/forgot-password', '/reset-password', '/select-tenant'];
        const currentPath = window.location.pathname;
        const isPublicRoute =
          publicRoutes.includes(currentPath)
          // Published AI agent experience (``<Route path="/public/agent/:slug">``
          // in App.js) is rendered outside ProtectedRoute for anonymous
          // visitors. Any background 401 (e.g. auth-context probes) must
          // NOT bounce the visitor to /login.
          || currentPath.startsWith('/public/agent/');
        const isAuthMeRequest = originalRequest.url && (
          originalRequest.url.includes('/auth/me') || 
          originalRequest.url.endsWith('/me')
        );
        
        // Don't redirect if:
        // 1. We're on a public route, OR
        // 2. This was an /auth/me request (checking authentication status)
        if (!isPublicRoute && currentPath !== '/login' && !isAuthMeRequest) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
